// src/oura/components/visibility/VerdictFix.tsx
//
// Correct one engine's verdict on one question (Jack, 22 Oct 2026). Oura reads
// each answer as Named or Not named; when that read is wrong (Brickhouse Q25:
// Google's AI "named" them because it matched the area name), staff set it
// right here instead of asking Ipul. Nothing is asked again, so it is free;
// the server recounts the score and keeps who, when and why.

import { useState } from "react";
import { correctVerdict, undoVerdict, type AeoVerdictCorrection, type AeoVerdictEngine, type AeoVerdictReason } from "@service/aeo";
import { PillButton, SURFACE, TYPE, Tag } from "@oura/ui";
import { cn } from "../../lib/cn";
import type { VisEngine } from "../../visibility/model";

const ROUTE_ENGINE: Record<VisEngine, AeoVerdictEngine> = { chatgpt: "chatgpt", gemini: "gemini", ai_overview: "google_ai" };

const REASONS: Array<{ value: AeoVerdictReason; label: string; when: "named" | "not" | "any" }> = [
    { value: "other_business", label: "It named a different business with a similar name", when: "named" },
    { value: "area_name", label: "It matched the area name, not the business", when: "named" },
    { value: "old_branch", label: "It named a closed or old branch", when: "named" },
    { value: "missed_name", label: "It did name the business, Oura missed it", when: "not" },
    { value: "other", label: "Something else", when: "any" },
];

const REASON_WORDS: Record<AeoVerdictReason, string> = {
    other_business: "named a different business",
    area_name: "matched the area name, not the business",
    old_branch: "named a closed or old branch",
    missed_name: "Oura missed the name",
    other: "other",
};

/** Where a correction is made: the latest check of a live business, or a sample. */
export interface VerdictFixTarget {
    assessmentId: string;
    sample: boolean;
}

export function VerdictFixForm({
    target,
    n,
    engine,
    engineLabel,
    readNamed,
    onCancel,
    onSaved,
}: {
    target: VerdictFixTarget;
    n: number;
    engine: VisEngine;
    engineLabel: string;
    /** What Oura read: the correction is the other way. */
    readNamed: boolean;
    onCancel: () => void;
    onSaved: (fix: AeoVerdictCorrection) => void;
}) {
    const named = !readNamed;
    const reasons = REASONS.filter((r) => r.when === "any" || r.when === (readNamed ? "named" : "not"));
    const [reason, setReason] = useState<AeoVerdictReason | null>(null);
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const needsNote = reason === "other" && !note.trim();

    const save = async () => {
        if (!reason || needsNote) return;
        setBusy(true);
        setError(null);
        // The note box shows only for "Something else", so only that reason sends it.
        const sentNote = reason === "other" ? note.trim() : "";
        const res = await correctVerdict({ assessmentId: target.assessmentId, n, engine: ROUTE_ENGINE[engine], named, reason, note: sentNote, sample: target.sample });
        setBusy(false);
        if (!res.ok) { setError(res.message); return; }
        onSaved({ named, was: readNamed, reason, note: sentNote || null, at: new Date().toISOString(), by: null });
    };

    return (
        <section className={cn(SURFACE.block, "p-5")}>
            <p className={cn(TYPE.tt, "m-0 text-gray-900")}>Correct {engineLabel} for this question</p>
            <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>
                Oura read this answer as {readNamed ? "Named" : "Not named"}. Saving changes it to {named ? "Named" : "Not named"}. Nothing is asked again, so it costs nothing.
            </p>
            <fieldset className="m-0 mt-4 border-0 p-0">
                <legend className={cn(TYPE.lab, "p-0")}>Why</legend>
                <div className="mt-1.5 space-y-1.5">
                    {reasons.map((r) => (
                        <label key={r.value} className={cn(TYPE.tb, "flex cursor-pointer items-center gap-2 text-gray-900")}>
                            <input type="radio" name={`verdict-reason-${n}`} checked={reason === r.value} onChange={() => setReason(r.value)} />
                            {r.label}
                        </label>
                    ))}
                </div>
            </fieldset>
            {reason === "other" && (
                <input
                    aria-label="What was wrong"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={200}
                    placeholder="What was wrong"
                    className={cn(TYPE.tb, "mt-2 block h-10 w-full rounded-xl border-0 bg-gray-50 px-3 text-gray-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600")}
                />
            )}
            <p className={cn(TYPE.tc, "m-0 mt-4 rounded-xl bg-gray-50 p-3 text-gray-600")}>
                The score is recounted with the correction and the client sees the corrected number. Your name and reason stay on the question.
            </p>
            {error && <p role="alert" className={cn(TYPE.tc, "m-0 mt-2 text-rose-600")}>{error}</p>}
            <div className="mt-4 flex justify-end gap-2">
                <PillButton onClick={onCancel}>Cancel</PillButton>
                <PillButton variant="primary" busy={busy} disabled={!reason || needsNote} onClick={() => void save()}>Save correction</PillButton>
            </div>
        </section>
    );
}

/** A correction already on this question, with Undo. */
export function VerdictFixNote({
    target,
    n,
    engine,
    engineLabel,
    fix,
    onUndone,
}: {
    target?: VerdictFixTarget;
    n: number;
    engine: VisEngine;
    engineLabel: string;
    fix: AeoVerdictCorrection;
    onUndone: () => void;
}) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const day = fix.at ? new Date(fix.at).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : null;
    const who = [fix.by, day].filter(Boolean).join(", ");
    const undo = async () => {
        if (!target) return;
        setBusy(true);
        setError(null);
        const res = await undoVerdict({ assessmentId: target.assessmentId, n, engine: ROUTE_ENGINE[engine], sample: target.sample });
        setBusy(false);
        if (!res.ok) { setError(res.message); return; }
        onUndone();
    };
    return (
        <section className={cn(SURFACE.block, "flex items-start gap-3 p-4")}>
            <Tag tone="amber">Corrected</Tag>
            <div className="min-w-0 flex-1">
                <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>{engineLabel} changed to {fix.named ? "Named" : "Not named"}</p>
                <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                    {who ? `${who}: ` : ""}{fix.reason === "other" && fix.note ? fix.note : REASON_WORDS[fix.reason]}.
                </p>
                {error && <p role="alert" className={cn(TYPE.tc, "m-0 mt-1 text-rose-600")}>{error}</p>}
            </div>
            {target && <PillButton size="sm" variant="text" busy={busy} onClick={() => void undo()}>Undo</PillButton>}
        </section>
    );
}
